import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, FileCheck2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money } from "../lib/api";
import { Vial } from "./Vial";

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group glass relative overflow-hidden rounded-2xl p-6 transition-colors duration-300 hover:border-[#E8E9EB]/30"
      data-testid={`product-card-${product.id}`}
    >
      <div className="flex items-start justify-between">
        <span className="font-mono text-[10px] tracking-[0.25em] text-[#E8E9EB]">{product.category.toUpperCase()}</span>
        <div className="flex items-center gap-2">
          <span className="rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] text-slate-400">
            {product.purity} HPLC
          </span>
          <span
            data-testid={`product-stock-${product.id}`}
            className={`flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 font-mono text-[9px] tracking-[0.1em] ${
              product.in_stock !== false
                ? "border-emerald-400/25 text-emerald-400"
                : "border-red-400/25 text-red-400"
            }`}
          >
            <span className={`h-1 w-1 rounded-full ${product.in_stock !== false ? "bg-emerald-400" : "bg-red-400"}`} />
            {product.in_stock !== false ? "IN STOCK" : "OUT OF STOCK"}
          </span>
        </div>
      </div>

      <Link to={`/products/${product.id}`} data-testid={`product-detail-link-${product.id}`} className="mt-4 block">
        <div className="flex justify-center py-4 transition-transform duration-500 group-hover:scale-105">
          <Vial code={product.code} pid={product.id} glow={false} className="w-44 sm:w-48" />
        </div>
        <h3 className="mt-2 font-display text-xl font-semibold tracking-tight text-white">
          {product.name} <span className="font-mono text-sm font-medium tracking-normal text-slate-400">{product.dose}</span>
        </h3>
        <p className="mt-1 text-sm text-slate-500">{product.subtitle}</p>
      </Link>

      <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-4">
        <span className="font-mono text-lg text-white" data-testid={`product-price-${product.id}`}>{money(product.price)}</span>
        <div className="flex items-center gap-2">
          {product.coa && (
            <Link
              to={`/coa/${product.id}`}
              data-testid={`product-coa-link-${product.id}`}
              title="Certificate of Analysis"
              className="rounded-full border border-white/10 p-2.5 text-slate-400 transition-colors duration-200 hover:border-[#E8E9EB]/50 hover:text-[#E8E9EB]"
            >
              <FileCheck2 className="h-4 w-4" />
            </Link>
          )}
          <Link
            to={`/products/${product.id}`}
            data-testid={`product-view-${product.id}`}
            className="rounded-full border border-white/10 p-2.5 text-slate-400 transition-colors duration-200 hover:border-[#E8E9EB]/50 hover:text-white"
          >
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <button
            onClick={() => addItem(product)}
            disabled={product.in_stock === false}
            data-testid={`add-to-cart-${product.id}`}
            className="rounded-full bg-[#6E7683] p-2.5 text-white transition-colors duration-200 hover:bg-[#E8E9EB] hover:text-[#050811] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-[#6E7683] disabled:hover:text-white"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.article>
  );
};
